import { useState } from 'react';
import {
  useAICostDashboard,
  useUpdateAIBudget,
  useSyncFromAnthropic,
  useResetToCalculatedUsage,
} from '@/hooks/useAI';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  DollarSign,
  TrendingUp,
  Calendar,
  RefreshCw,
  Cloud,
  Calculator,
  Edit2,
  Check,
  X,
} from 'lucide-react';

/**
 * Format currency
 */
function formatCurrency(value: number): string {
  return `$${value.toFixed(2)}`;
}

/**
 * Format large numbers
 */
function formatNumber(value: number): string {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toString();
}

/**
 * Period Stats Card
 */
function PeriodCard({
  title,
  cost,
  tokens,
  requests,
  highlight = false,
}: {
  title: string;
  cost: number;
  tokens: number;
  requests: number;
  highlight?: boolean;
}) {
  return (
    <Card className={highlight ? 'border-primary' : ''}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{formatCurrency(cost)}</div>
        <div className="text-xs text-muted-foreground mt-1">
          {formatNumber(tokens)} tokens | {requests} requests
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Budget Editor Component
 */
function BudgetEditor({
  currentBudget,
  currentUsed,
  onSave,
  onCancel,
  isPending,
}: {
  currentBudget: number;
  currentUsed: number;
  onSave: (budget: number, used: number) => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  const [budget, setBudget] = useState(currentBudget.toString());
  const [used, setUsed] = useState(currentUsed.toString());

  return (
    <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label htmlFor="budget">Monthly Budget ($)</Label>
          <Input
            id="budget"
            type="number"
            step="0.01"
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            placeholder="100.00"
          />
        </div>
        <div>
          <Label htmlFor="used">Used Amount ($)</Label>
          <Input
            id="used"
            type="number"
            step="0.01"
            value={used}
            onChange={(e) => setUsed(e.target.value)}
            placeholder="0.00"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Enter amount from Anthropic console
          </p>
        </div>
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={() => onSave(parseFloat(budget), parseFloat(used))}
          disabled={isPending}
        >
          {isPending ? <LoadingSpinner className="h-4 w-4 mr-2" /> : <Check className="h-4 w-4 mr-2" />}
          Save
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          <X className="h-4 w-4 mr-2" />
          Cancel
        </Button>
      </div>
    </div>
  );
}

/**
 * Daily Cost Chart (simple bar representation)
 */
function DailyCostChart({ data }: { data: Array<{ date: string; cost: number }> }) {
  if (!data || data.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-8">
        No cost data available yet
      </div>
    );
  }

  const maxCost = Math.max(...data.map((d) => d.cost), 0.01);

  return (
    <div className="space-y-2">
      <div className="flex items-end gap-1 h-40">
        {data.slice(-30).map((day) => {
          const height = (day.cost / maxCost) * 100;
          return (
            <div
              key={day.date}
              className="flex-1 bg-primary/20 hover:bg-primary/40 rounded-t transition-colors relative group"
              style={{ height: `${Math.max(height, 2)}%` }}
            >
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-popover border rounded px-2 py-1 text-xs whitespace-nowrap z-10">
                {day.date}: {formatCurrency(day.cost)}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{data[0]?.date}</span>
        <span>{data[data.length - 1]?.date}</span>
      </div>
    </div>
  );
}

export default function AICosts() {
  const { data, isLoading, refetch } = useAICostDashboard();
  const updateBudget = useUpdateAIBudget();
  const syncAnthropic = useSyncFromAnthropic();
  const resetUsage = useResetToCalculatedUsage();

  const [isEditing, setIsEditing] = useState(false);

  const handleSaveBudget = async (budget: number, used: number) => {
    await updateBudget.mutateAsync({ monthlyBudget: budget, usedAmount: used });
    setIsEditing(false);
  };

  const handleSyncAnthropic = async () => {
    try {
      await syncAnthropic.mutateAsync();
    } catch (error) {
      // Error handling - the mutation will show error state
    }
  };

  const handleResetUsage = async () => {
    await resetUsage.mutateAsync();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner className="h-8 w-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">AI Costs</h1>
          <p className="text-muted-foreground">
            Monitor and manage your AI API spending
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Budget Overview */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Monthly Budget
              </CardTitle>
              <CardDescription>
                {data?.budget.syncedFromPortal
                  ? 'Synced from Anthropic portal'
                  : 'Based on local tracking'}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              {!isEditing && (
                <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit Budget
                </Button>
              )}
              {data?.anthropicSync.available && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSyncAnthropic}
                  disabled={syncAnthropic.isPending}
                >
                  {syncAnthropic.isPending ? (
                    <LoadingSpinner className="h-4 w-4 mr-2" />
                  ) : (
                    <Cloud className="h-4 w-4 mr-2" />
                  )}
                  Sync from Anthropic
                </Button>
              )}
              {data?.budget.syncedFromPortal && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleResetUsage}
                  disabled={resetUsage.isPending}
                >
                  <Calculator className="h-4 w-4 mr-2" />
                  Use Calculated
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <BudgetEditor
              currentBudget={data?.budget.monthly || 100}
              currentUsed={data?.budget.used || 0}
              onSave={handleSaveBudget}
              onCancel={() => setIsEditing(false)}
              isPending={updateBudget.isPending}
            />
          ) : (
            <>
              {/* Budget Stats */}
              <div className="grid gap-6 md:grid-cols-4 mb-6">
                <div>
                  <p className="text-sm text-muted-foreground">Monthly Budget</p>
                  <p className="text-3xl font-bold">{formatCurrency(data?.budget.monthly || 0)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Used This Month</p>
                  <p className="text-3xl font-bold text-orange-600">
                    {formatCurrency(data?.budget.used || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Remaining</p>
                  <p
                    className={`text-3xl font-bold ${
                      (data?.budget.remaining || 0) < 10 ? 'text-red-600' : 'text-green-600'
                    }`}
                  >
                    {formatCurrency(data?.budget.remaining || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Budget Used</p>
                  <p className="text-3xl font-bold">{(data?.budget.percentUsed || 0).toFixed(1)}%</p>
                </div>
              </div>

              {/* Budget Progress Bar */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Budget Usage</span>
                  <span>
                    {formatCurrency(data?.budget.used || 0)} / {formatCurrency(data?.budget.monthly || 0)}
                  </span>
                </div>
                <div className="h-4 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      (data?.budget.percentUsed || 0) > 90
                        ? 'bg-red-500'
                        : (data?.budget.percentUsed || 0) > 70
                        ? 'bg-yellow-500'
                        : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(100, data?.budget.percentUsed || 0)}%` }}
                  />
                </div>
                {(data?.budget.percentUsed || 0) > 70 && (
                  <p
                    className={`text-xs mt-1 ${
                      (data?.budget.percentUsed || 0) > 90 ? 'text-red-600' : 'text-yellow-600'
                    }`}
                  >
                    {(data?.budget.percentUsed || 0) > 90
                      ? 'Critical: Budget almost exhausted!'
                      : 'Warning: Budget usage is high'}
                  </p>
                )}
              </div>

              {/* Show calculated vs synced */}
              {data?.budget.syncedFromPortal && data.budget.calculatedUsage !== data.budget.used && (
                <p className="text-xs text-muted-foreground mt-2">
                  Local calculated: {formatCurrency(data.budget.calculatedUsage)} | Synced:{' '}
                  {formatCurrency(data.budget.used)}
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Period Summary Cards */}
      <div>
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Cost Summary
        </h2>
        <div className="grid gap-4 md:grid-cols-4">
          <PeriodCard
            title="Today"
            cost={data?.periods.today.cost || 0}
            tokens={data?.periods.today.tokens || 0}
            requests={data?.periods.today.requests || 0}
          />
          <PeriodCard
            title="This Week"
            cost={data?.periods.thisWeek.cost || 0}
            tokens={data?.periods.thisWeek.tokens || 0}
            requests={data?.periods.thisWeek.requests || 0}
          />
          <PeriodCard
            title="This Month"
            cost={data?.periods.thisMonth.cost || 0}
            tokens={data?.periods.thisMonth.tokens || 0}
            requests={data?.periods.thisMonth.requests || 0}
            highlight
          />
          <PeriodCard
            title="Last 30 Days"
            cost={data?.periods.last30Days.cost || 0}
            tokens={data?.periods.last30Days.tokens || 0}
            requests={data?.periods.last30Days.requests || 0}
          />
        </div>
      </div>

      {/* Daily Cost Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Daily Costs (Last 30 Days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DailyCostChart data={data?.dailyCosts || []} />
        </CardContent>
      </Card>

      {/* Breakdown Cards */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* By Provider */}
        <Card>
          <CardHeader>
            <CardTitle>Cost by Provider</CardTitle>
          </CardHeader>
          <CardContent>
            {data?.byProvider && data.byProvider.length > 0 ? (
              <div className="space-y-3">
                {data.byProvider.map((p) => (
                  <div key={p._id} className="flex justify-between items-center">
                    <div>
                      <span className="font-medium capitalize">{p._id}</span>
                      <span className="text-sm text-muted-foreground ml-2">
                        ({p.count} requests)
                      </span>
                    </div>
                    <span className="font-mono">{formatCurrency(p.cost)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground">No provider data</p>
            )}
          </CardContent>
        </Card>

        {/* By Request Type */}
        <Card>
          <CardHeader>
            <CardTitle>Cost by Type</CardTitle>
          </CardHeader>
          <CardContent>
            {data?.byRequestType && data.byRequestType.length > 0 ? (
              <div className="space-y-3">
                {data.byRequestType.map((t) => (
                  <div key={t._id} className="flex justify-between items-center">
                    <div>
                      <span className="font-medium capitalize">
                        {t._id.replace(/_/g, ' ')}
                      </span>
                      <span className="text-sm text-muted-foreground ml-2">
                        ({t.count} requests)
                      </span>
                    </div>
                    <span className="font-mono">{formatCurrency(t.cost)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground">No request type data</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Sync Status */}
      {!data?.anthropicSync.available && (
        <Card className="bg-muted/50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Cloud className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="font-medium">Automatic Sync Not Available</p>
                <p className="text-sm text-muted-foreground">
                  To enable automatic sync from Anthropic, set the{' '}
                  <code className="bg-muted px-1 rounded">ANTHROPIC_ADMIN_API_KEY</code>{' '}
                  environment variable. Get your Admin API key from{' '}
                  <a
                    href="https://console.anthropic.com/settings/admin-keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline"
                  >
                    console.anthropic.com
                  </a>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
